import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from '../icons/CalendarIcon';
import { ChevronDownIcon } from '../icons/ChevronDownIcon';
import { ChevronUpIcon } from '../icons/ChevronUpIcon';
import Dropdown from '../Dropdown/Dropdown';
import DatePickerV2, { getModeAnchorDate } from './DatePickerV2';
import { StyledButtonSelectOpener } from '../SingleSelectDropdown/styles';
import type { DatePickerV2Props, DatePickerV2TriggerProps, DateRange } from './types';
import { CUSTOM_PRESET_VALUE } from './constants';
import { resolveRangeSelection } from './utils';
const DEFAULT_DISPLAY_FORMAT = 'MMM do, yyyy';
function getTriggerLabel(props: DatePickerV2Props, placeholder: string, displayFormat: string): string {
  switch (props.mode) {
    case 'single':
    case 'single-comparison':
      return props.selectedDate == null ? placeholder : format(props.selectedDate, displayFormat);
    case 'range':
    case 'range-comparison':
    case 'range-overlap':
      {
        const range = resolveRangeSelection(props.selectedRange, props.selectedPreset, props.presets?.getDateRangeFromPreset, props.maxDate, props.minDate).range;
        if (range == null) return placeholder;
        const fromLabel = format(range.from, displayFormat);
        const toLabel = range.to == null ? '' : ` - ${format(range.to, displayFormat)}`;
        return `${fromLabel}${toLabel}`;
      }
  }
}
interface PendingSelection {
  date: Date | null;
  comparisonDate: Date | null;
  range: DateRange | null;
  comparisonRange: DateRange | null;
  presetValue: string;
  comparisonPreset: string | undefined;
}
const seedModeSelection = (props: DatePickerV2Props): Omit<PendingSelection, 'comparisonPreset'> => {
  switch (props.mode) {
    case 'single':
      return {
        date: props.selectedDate,
        comparisonDate: null,
        range: null,
        comparisonRange: null,
        presetValue: CUSTOM_PRESET_VALUE
      };
    case 'single-comparison':
      return {
        date: props.selectedDate,
        comparisonDate: props.comparisonDate,
        range: null,
        comparisonRange: null,
        presetValue: CUSTOM_PRESET_VALUE
      };
    case 'range':
      {
        const selection = resolveRangeSelection(props.selectedRange, props.selectedPreset, props.presets?.getDateRangeFromPreset, props.maxDate, props.minDate);
        return {
          date: null,
          comparisonDate: null,
          range: selection.range,
          comparisonRange: null,
          presetValue: selection.presetValue
        };
      }
    case 'range-comparison':
    case 'range-overlap':
      {
        const selection = resolveRangeSelection(props.selectedRange, props.selectedPreset, props.presets?.getDateRangeFromPreset, props.maxDate, props.minDate);
        return {
          date: null,
          comparisonDate: null,
          range: selection.range,
          comparisonRange: props.comparisonRange,
          presetValue: selection.presetValue
        };
      }
  }
};
const seedPending = (props: DatePickerV2Props): PendingSelection => ({
  ...seedModeSelection(props),
  comparisonPreset: props.selectedComparisonPreset
});
const isPendingEmpty = (props: DatePickerV2Props, pending: PendingSelection): boolean => {
  switch (props.mode) {
    case 'single':
      return pending.date == null;
    case 'single-comparison':
      return pending.date == null && pending.comparisonDate == null;
    case 'range':
    case 'range-comparison':
    case 'range-overlap':
      return pending.range == null;
  }
};
const DatePickerV2Trigger = ({
  placeholder = 'Select date',
  displayFormat = DEFAULT_DISPLAY_FORMAT,
  ref,
  ...datePickerProps
}: DatePickerV2TriggerProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState<PendingSelection>(() => seedPending(datePickerProps));
  const [viewMonth, setViewMonth] = useState<Date | undefined>(undefined);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const setAnchorRef = (node: HTMLButtonElement | null): void => {
    anchorRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref != null) {
      ref.current = node;
    }
  };
  const {
    disabled
  } = datePickerProps;
  const label = getTriggerLabel(datePickerProps, placeholder, displayFormat);

  // Keep a ref so the open effect always seeds from the latest committed props,
  // even if the consumer updates them while the dropdown is closed.
  const datePickerPropsRef = useRef(datePickerProps);
  datePickerPropsRef.current = datePickerProps;

  // On open: reseed pending state and snapshot the month to centre the view on,
  // so the calendar shows the committed value and doesn't jerk mid-selection.
  useEffect(() => {
    if (isOpen) {
      setPending(seedPending(datePickerPropsRef.current));
      setViewMonth(getModeAnchorDate(datePickerPropsRef.current) ?? new Date());
    }
  }, [isOpen]);
  const handleToggle = (): void => {
    if (disabled === true) return;
    setIsOpen(prev => !prev);
  };
  const handleCancel = (): void => {
    setPending(seedPending(datePickerPropsRef.current));
    setIsOpen(false);
  };

  // Runs inside DatePickerV2's Apply click, ahead of the consumer's own apply hook
  // and of handleApply, so a consumer reading state written here already sees the
  // committed comparison preset when it commits the ranges.
  const handleApplyDateRangeClick = (): void => {
    if (pending.comparisonPreset != null && pending.comparisonPreset !== datePickerProps.selectedComparisonPreset) {
      datePickerProps.onComparisonPresetChange?.(pending.comparisonPreset);
    }
    datePickerProps.onApplyDateRangeClick?.();
  };
  const handleApply = (): void => {
    switch (datePickerProps.mode) {
      case 'single':
        datePickerProps.onSelectDate(pending.date);
        break;
      case 'single-comparison':
        datePickerProps.onSelectDate(pending.date);
        datePickerProps.onSelectComparisonDate(pending.comparisonDate);
        break;
      case 'range':
        datePickerProps.onSelectRange(pending.range, pending.presetValue);
        break;
      case 'range-comparison':
      case 'range-overlap':
        datePickerProps.onSelectRange(pending.range, pending.presetValue);
        datePickerProps.onSelectComparisonRange(pending.comparisonRange);
        break;
    }
    setIsOpen(false);
  };

  // Build a buffered version of the picker props: selection values come from
  // pending state, and consumer onSelect* callbacks are intercepted to update
  // pending state instead of committing. The comparison preset is buffered the
  // same way at the render site below.
  const buildBufferedProps = (): DatePickerV2Props => {
    switch (datePickerProps.mode) {
      case 'single':
        return {
          ...datePickerProps,
          selectedDate: pending.date,
          onSelectDate: date_0 => {
            setPending(prev_5 => ({
              ...prev_5,
              date: date_0
            }));
          }
        };
      case 'single-comparison':
        return {
          ...datePickerProps,
          selectedDate: pending.date,
          comparisonDate: pending.comparisonDate,
          onSelectDate: date => {
            setPending(prev_3 => ({
              ...prev_3,
              date
            }));
          },
          onSelectComparisonDate: comparisonDate => {
            setPending(prev_4 => ({
              ...prev_4,
              comparisonDate
            }));
          }
        };
      case 'range':
        return {
          ...datePickerProps,
          selectedRange: pending.range,
          selectedPreset: pending.presetValue,
          onSelectRange: (range_0, presetValue_0) => {
            setPending(prev_2 => ({
              ...prev_2,
              range: range_0,
              presetValue: presetValue_0
            }));
          }
        };
      case 'range-comparison':
      case 'range-overlap':
        return {
          ...datePickerProps,
          selectedRange: pending.range,
          selectedPreset: pending.presetValue,
          comparisonRange: pending.comparisonRange,
          onSelectRange: (range, presetValue) => {
            setPending(prev_0 => ({
              ...prev_0,
              range,
              presetValue
            }));
          },
          onSelectComparisonRange: comparisonRange => {
            setPending(prev_1 => ({
              ...prev_1,
              comparisonRange
            }));
          }
        };
    }
  };
  const bufferedProps = buildBufferedProps();
  return <>
      <StyledButtonSelectOpener ref={setAnchorRef} variant="outlined" onClick={handleToggle} disabled={disabled} isDropdownOpen={isOpen} hasOptionsSelectedAndAllowUndefinedValue={false} width={datePickerProps.mode === 'single' ? '186px' : '298px'} leftIcon={<CalendarIcon />} rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}>
        {label}
      </StyledButtonSelectOpener>
      <Dropdown anchorEl={anchorRef.current} open={isOpen} onClose={handleCancel} disablePadding maxHeight="640px">
        <DatePickerV2 {...bufferedProps} selectedComparisonPreset={pending.comparisonPreset} onComparisonPresetChange={comparisonPreset => {
        setPending(prev_6 => ({
          ...prev_6,
          comparisonPreset
        }));
      }} onApplyDateRangeClick={handleApplyDateRangeClick} onApply={handleApply} onCancel={handleCancel} isApplyDisabled={isPendingEmpty(datePickerProps, pending)} viewMonth={viewMonth} />
      </Dropdown>
    </>;
};
export default DatePickerV2Trigger;